/**
 * Formatting helpers.
 *
 * Centralised because a dashboard that spells money three different ways is hard
 * to scan, and because the South African conventions here (R prefix, space as
 * the thousands separator) are easy to get subtly wrong by hand.
 */

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const ZAR_COMPACT = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const NUMBER = new Intl.NumberFormat('en-ZA')

export const money = (value) => ZAR.format(Number(value) || 0)

/** For axis ticks and stat tiles, where cents are noise. */
export const moneyShort = (value) => {
  const n = Number(value) || 0
  if (Math.abs(n) >= 1000000) return `R${(n / 1000000).toFixed(1)}m`
  if (Math.abs(n) >= 10000) return `R${Math.round(n / 1000)}k`
  return ZAR_COMPACT.format(n)
}

export const number = (value) => NUMBER.format(Number(value) || 0)

export const percent = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '—'
    : `${Number(value).toFixed(digits)}%`

/** Signed, for deltas. The sign is the whole point, so it is never dropped. */
export const signedPercent = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  const n = Number(value)
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`
}

const DATE = new Intl.DateTimeFormat('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
const DATE_SHORT = new Intl.DateTimeFormat('en-ZA', { day: '2-digit', month: 'short' })
const DATETIME = new Intl.DateTimeFormat('en-ZA', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

export const formatDate = (value) => (value ? DATE.format(new Date(value)) : '—')
export const formatDateShort = (value) => (value ? DATE_SHORT.format(new Date(value)) : '—')
export const formatDateTime = (value) => (value ? DATETIME.format(new Date(value)) : '—')

/** "3 days ago" — for activity feeds, where exact timestamps are clutter. */
export const relativeTime = (value) => {
  if (!value) return '—'
  const then = new Date(value).getTime()
  const diff = Date.now() - then
  const minutes = Math.round(diff / 60000)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatDate(value)
}

/**
 * Percentage change between two periods.
 *
 * Returns null rather than Infinity or 100% when the earlier period was zero.
 * "Up from nothing" is not a percentage, and rendering it as +100% is a lie the
 * reader cannot detect.
 */
export const changePct = (current, previous) => {
  const a = Number(current) || 0
  const b = Number(previous) || 0
  if (b === 0) return null
  return ((a - b) / b) * 100
}

/** Human label for an order status. */
export const STATUS_LABELS = {
  pending: 'Awaiting payment',
  paid: 'Paid',
  processing: 'Packing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

export const statusLabel = (status) => STATUS_LABELS[status] ?? status
